import React, { useState, useRef, useEffect } from "react";
import { SITELOCATION_GLOBAL_STATE, MESSAGE_FLEET_GLOBAL_STATES, UPDATED_CAR_DATA_STATE, MAP_GLOBAL_STATES, SITEID_GLOBAL_STATE } from "globalStore/globalStore";
import { getFeaturesList, isUserSupport, isUserAdmin, getFeatureSetOption, carType } from "../../../constant";
import { MapCarIcon, TaggedCarIconComponent, LocationIcon } from "../MapIcons";
import { useHookstate } from "@hookstate/core";
import { useSelector, useDispatch } from "react-redux";
import { uuid } from "_helperFunctions/uuid";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";

export const CarMarkers = ({
    carData,
    mapData,
    mapsData,
    setEditModel,
    zoomPosition
}) => {
    const { siteLocationGlobalData } = useHookstate(SITELOCATION_GLOBAL_STATE);
    const { tagCarFromFleet } = useHookstate(MESSAGE_FLEET_GLOBAL_STATES);
    const { updatedCarData } = useHookstate(UPDATED_CAR_DATA_STATE);
    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const {
        fleetCarsGlobalState,
        specialCarsGlobalState,
        memberCarsGlobalState,
        marshalCarsGlobalState,
        beverageCarsGlobalState,
        notInUseCarsGlobalState,
        carsNotCommunicatingGlobalState
    } = useHookstate(MAP_GLOBAL_STATES);

    const siteLocationGlobalDataValue = siteLocationGlobalData.get();
    const tagCarFromFleetValue = tagCarFromFleet.get();

    const { siteSettingsData, getUserRole, siteLocationsData, carProductData } = useSelector((state) => state.commonReducers) || [];
    const dispatch = useDispatch();

    const markerRefs = useRef({});

    const [carProductDataMap, setCarProductDataMap] = useState({});
    const [overlaps, setOverlaps] = useState({});
    const [markerHovered, setMarkerHovered] = useState(false);

    // Fetch product data for all cars
    useEffect(() => {
        if (carData && carData.length > 0) {
            carData.forEach((carItem) => {
                if (carItem?.Car?.GpsiCarId) {
                    dispatch(COMMON_SERVICES.getProductData(carItem.Car.GpsiCarId));
                }
            });
        }
    }, [carData, dispatch]);

    // Sync Redux carProductData to local map whenever it updates
    useEffect(() => {
        if (carProductData && carProductData.GpsiCarId) {
            setCarProductDataMap((prev) => ({
                ...prev,
                [carProductData.GpsiCarId]: carProductData
            }));
        }
    }, [carProductData]);

    useEffect(() => {
        if (Object.keys(updatedCarData.get()).length > 0 && Object.keys(markerRefs.current).length > 0) {
            var filteredCarData = JSON.parse(updatedCarData.get());

            const hasMatchingMarker = filteredCarData.some(car => markerRefs.current[car.Car.CarNumber]);
            const isSiteIdMatching = filteredCarData.some(car => car.Car.SiteId === parseInt(siteId.get()));

            if (!hasMatchingMarker || !isSiteIdMatching) {
                return;
            }

            if (!fleetCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.normal)
            }

            if (!specialCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.handicap)
            }

            if (!memberCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.member)
            }

            if (!marshalCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.marshal)
            }

            if (!beverageCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.bevcart)
            }

            if (!notInUseCarsGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.notInUse)
            }

            if (!carsNotCommunicatingGlobalState.get()) {
                filteredCarData = filteredCarData.filter(x => x.Car.CarType != carType.notCommunicating)
            }

            markerRefs.current = Object.fromEntries(Object.entries(markerRefs.current).filter(([, value]) => value));

            const overlaps = calculateOverlaps(markerRefs, filteredCarData);
            setOverlaps(overlaps);
        }
    }, [markerHovered,
        zoomPosition,
        updatedCarData.get(),
        fleetCarsGlobalState.get(),
        specialCarsGlobalState.get(),
        memberCarsGlobalState.get(),
        marshalCarsGlobalState.get(),
        beverageCarsGlobalState.get(),
        notInUseCarsGlobalState.get(),
        carsNotCommunicatingGlobalState.get()
    ]);

    const shouldShowCarMarkers = () => {
        return ((!siteLocationGlobalDataValue && carData?.length !== 0) ||
            (tagCarFromFleetValue && !siteLocationGlobalDataValue && tagCarFromFleetValue.length === 0)) &&
            localStorage.getItem("mapCarsSelectAll") === "true" &&
            !(getFeatureSetOption(siteSettingsData && siteSettingsData?.Features, getFeaturesList().real_time_tracking) &&
                !(isUserAdmin(getUserRole && getUserRole.Role) || isUserSupport(getUserRole && getUserRole.Role)));
    }

    const shouldShowTagCar = () => {
        return !siteLocationGlobalDataValue && tagCarFromFleetValue && tagCarFromFleetValue !== 0;
    }

    const calculateOverlaps = (markerRefs, carDataList) => {
        const rects = {};
        const result = {};

        // Create quick lookup map for cars
        const carMap = {};

        carDataList.forEach((car) => {
            carMap[car.Car.CarNumber.toString()] = car;
        });

        // Get bounding rects
        Object.entries(markerRefs.current).forEach(([carNumber, el]) => {
            if (el?.carMarkerRef?.current) {
                rects[carNumber] = el.carMarkerRef.current.getBoundingClientRect();
            }
        });

        // Calculate overlaps
        Object.entries(rects).forEach(([idA, rectA]) => {
            const keyCar = carMap[idA];

            const overlappingDisplay = [];

            // Add primary car first
            if (keyCar) {
                overlappingDisplay.push({
                    carNumber: keyCar.Car.CarNumber,
                    car: keyCar,
                    holeNumber: keyCar.Car.Hole,
                    holeLocation: keyCar.Car.HoleLocation,
                    isKeyCar: true,
                });
            }

            Object.entries(rects).forEach(([idB, rectB]) => {
                if (idA === idB || rectA.width === 0) return;

                // Quick reject if rectangles don't intersect
                const noOverlap =
                    rectA.left > rectB.right ||
                    rectA.right < rectB.left ||
                    rectA.top > rectB.bottom ||
                    rectA.bottom < rectB.top;

                if (noOverlap) return;

                // Areas
                const areaA = rectA.width * rectA.height;
                const areaB = rectB.width * rectB.height;

                const smallerArea = Math.min(areaA, areaB);
                const requiredOverlapArea = smallerArea * 0.5;

                // Intersecting area
                const intersectingArea =
                    Math.max(0, Math.min(rectA.right, rectB.right) - Math.max(rectA.left, rectB.left)) *
                    Math.max(0, Math.min(rectA.bottom, rectB.bottom) - Math.max(rectA.top, rectB.top));

                const isOverlap = intersectingArea > requiredOverlapArea;

                if (!isOverlap) return;

                const overlappingCar = carMap[idB];

                if (!overlappingCar) return;

                overlappingDisplay.push({
                    carNumber: overlappingCar.Car.CarNumber,
                    car: overlappingCar,
                    holeNumber: overlappingCar.Car.Hole,
                    holeLocation: overlappingCar.Car.HoleLocation,
                    isKeyCar: false,
                });
            });

            result[idA] = {
                overlappingDisplay,
            };
        });

        return result;
    }

    return (
        <>
            {siteLocationGlobalData.get() && mapData && mapsData &&
                siteLocationsData.map((locations, index) => {
                    const lat = locations?.CenterPointLatitude;
                    const lng = locations?.CenterPointLongitude;

                    return (
                        <AdvancedMarker position={{ lat, lng }} key={`Location Marker: ${locations.Id}, index: ${index}`}>
                            <LocationIcon
                                style={{ position: "relative", zIndex: 9 }}
                                tracksViewChanges={false}
                                key={"mapSiteId" + index}
                                uniqueId={"mapSiteIdUni" + index}
                                Id={locations?.Id}
                                lat={locations?.CenterPointLatitude}
                                lng={locations?.CenterPointLongitude}
                                locationName={locations?.Name}
                                IsEUSite={locations?.IsEUSite}
                            />
                        </AdvancedMarker>
                    )
                })
            }

            {shouldShowCarMarkers() ? (
                carData.map((carItem, i) => {
                    if (carItem?.Car?.Latitude !== 0 && carItem?.Car?.Longitude !== 0) {
                        var lat = carItem?.Car?.Latitude;
                        var lng = carItem?.Car?.Longitude;

                        return (<AdvancedMarker
                            position={{ lat, lng }}
                            key={`${siteId.get()}-${carItem.Car.CarNumber}`}
                            anchorLeft="50%"
                            anchorTop="50%"
                            zIndex={1}>
                            <MapCarIcon
                                ref={(el) => { markerRefs.current[carItem.Car.CarNumber] = el; }}
                                carItem={carItem}
                                key={`${siteId.get()}-${carItem.Car.CarNumber}-icon`}
                                uniqueId={"carIconidUni" + i}
                                setEditModel={setEditModel}
                                mapsData={mapsData}
                                mapData={mapData}
                                overlapData={overlaps?.[carItem.Car.CarNumber]}
                                carProductData={carProductDataMap[carItem.Car.GpsiCarId]}
                                setMarkerHovered={setMarkerHovered}
                            />
                        </AdvancedMarker>
                        );
                    }
                })
            ) : shouldShowTagCar() &&
            <TaggedCarIconComponent
                key={uuid()}
                uniqueId={uuid()}
                lat={tagCarFromFleetValue && tagCarFromFleetValue[0]?.lat}
                lng={tagCarFromFleetValue && tagCarFromFleetValue[0]?.lag}
            />
            }
        </>
    );
}